import type { BuiltInTool } from './types'

/** Tools a task uses to triage self-improvement recommendations. */
export const OS_RECOMMENDATION_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.list_recommendations',
        family: 'os.recommendations',
        summary: 'List self-improvement recommendations: pending, decided, task-record, or outcomes to check.',
        description:
            'List self-improvement recommendations. view: pending (default, oldest first, to decide), decided (the last 60 days across every task, to spot recurrence), task-record (actioned for implementation in the last 14 days, to follow up their issues), or outcomes (the issues and pull requests decided recommendations cite whose outcome is not known yet: read each with your own tools and record it with os.record_issue_outcome). Optional limit.',
        inputSchema: {
            type: 'object',
            properties: {
                view: { type: 'string', enum: ['pending', 'decided', 'task-record', 'outcomes'] },
                limit: { type: 'integer', minimum: 1 },
            },
            additionalProperties: false,
        },
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        errors: [{ code: 'view_invalid', when: '`view` is not one of the four.', handling: 'tool_error' }],
        examples: [{ input: { view: 'pending', limit: 10 } }, { input: { view: 'outcomes' } }],
        public: true,
    },
    {
        name: 'os.record_issue_outcome',
        family: 'os.recommendations',
        summary: 'Record how an issue or pull request a recommendation cites ended.',
        description:
            'Record what became of an issue or pull request that decided recommendations cite, as you read it with your own tools: open, completed, not_planned, duplicate, merged (a pull request merged) or closed (closed with nothing more known, or a pull request closed unmerged). Every recommendation citing issueUrl keeps it as issueOutcome, which self-improvement reads so a change turned down is not proposed again. Pass issueUrl and outcome.',
        inputSchema: {
            type: 'object',
            properties: {
                issueUrl: {
                    type: 'string',
                    description: 'The issue or pull request URL, as the recommendation cites it',
                },
                outcome: {
                    type: 'string',
                    enum: ['open', 'completed', 'not_planned', 'duplicate', 'merged', 'closed'],
                },
            },
            required: ['issueUrl', 'outcome'],
            additionalProperties: false,
        },
        callers: ['run'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'Recording the same outcome twice leaves the same state.',
        errors: [
            {
                code: 'issue_url_invalid',
                when: '`issueUrl` is not an issue or pull request URL.',
                handling: 'tool_error',
            },
            { code: 'outcome_invalid', when: '`outcome` is not one of the six.', handling: 'tool_error' },
            {
                code: 'issue_not_cited',
                when: 'No recommendation cites `issueUrl`. Record outcomes for the URLs view `outcomes` lists.',
                handling: 'tool_error',
            },
        ],
        examples: [{ input: { issueUrl: 'https://git.example.com/acme/app/issues/12', outcome: 'completed' } }],
        public: true,
    },
    {
        name: 'os.get_recommendation',
        family: 'os.recommendations',
        summary: 'One recommendation with its evidence.',
        description:
            'One recommendation with its evidence: the source run (status, recent events, failed tool calls), task versions newer than the one the source run ran, and every other recommendation for the same task. Pass id.',
        inputSchema: {
            type: 'object',
            properties: { id: { type: 'string' } },
            required: ['id'],
            additionalProperties: false,
        },
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        errors: [{ code: 'id_required', when: '`id` is missing.', handling: 'tool_error' }],
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000001' } }],
        public: true,
    },
    {
        name: 'os.decide_recommendation',
        family: 'os.recommendations',
        summary: 'Decide one pending recommendation: decline, actioned, or implement.',
        description:
            'Decide one pending recommendation. decision: decline (reason required), actioned (issueUrl of the issue or pull request that takes the work, and lane: os, repo-commands or local-stack), or implement (the task-record lane: issueUrl of the issue that tracks it; starts the implementer). Cite an open issue or a merged pull request in the repository the work belongs to: the OS checks only that issueUrl is an issue or pull request URL. Pass id, decision, reason, issueUrl, lane.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string' },
                decision: { type: 'string', enum: ['decline', 'actioned', 'implement'] },
                reason: { type: 'string' },
                issueUrl: {
                    type: 'string',
                    description: 'An issue or pull request URL, e.g. https://<host>/<owner>/<repo>/issues/<n>',
                },
                lane: { type: 'string', enum: ['os', 'repo-commands', 'local-stack', 'task-record'] },
            },
            required: ['id', 'decision', 'reason'],
            additionalProperties: false,
        },
        callers: ['run'],
        effect: 'write',
        sideEffects: '`implement` starts the implementing task.',
        retry: 'idempotent',
        retryNote:
            'Keyed on the tool call: the same call sent twice decides once and returns the first answer. A different call on a decided recommendation is refused.',
        errors: [
            { code: 'id_required', when: '`id` is missing.', handling: 'tool_error' },
            {
                code: 'recommendation_not_pending:<status>',
                when: 'Someone else already decided it. Only a `pending` recommendation can be decided.',
                handling: 'tool_error',
            },
            { code: 'decision_invalid', when: '`decision` is not one of the three.', handling: 'tool_error' },
            { code: 'lane_invalid', when: '`actioned` without a valid `lane`.', handling: 'tool_error' },
            {
                code: 'task_record_uses_implement',
                when: '`actioned` with lane `task-record`: decide `implement` instead.',
                handling: 'tool_error',
            },
            {
                code: 'issue_url_invalid',
                when: '`actioned` or `implement` without an issue or pull request URL.',
                handling: 'tool_error',
            },
        ],
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000001',
                    decision: 'decline',
                    reason: 'Not reproduced on the source run',
                },
            },
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000002',
                    decision: 'actioned',
                    reason: 'Covered by the retry change',
                    issueUrl: 'https://git.example.com/acme/app/issues/12',
                    lane: 'os',
                },
            },
        ],
        public: true,
    },
]
